import { useEffect, useRef } from 'react';
import { Marker as MapLibreMarker, MarkerOptions, Popup } from 'maplibre-gl';
import { useMapContext } from './MapContext';

export interface MapMarkerProps {
  position: [number, number];
  color?: string;
  size?: number;
  onClick?: () => void;
  popup?: string;
  popupHtml?: string;
  draggable?: boolean;
  onDragEnd?: (position: [number, number]) => void;
  iconType?: 'default' | 'courier' | 'restaurant' | 'delivery' | 'home';
  bearing?: number | null;
}

export const MapMarker = ({
  position,
  color = '#E63946',
  size = 32,
  onClick,
  popup,
  popupHtml,
  draggable = false,
  onDragEnd,
  iconType = 'default',
  bearing = null,
}: MapMarkerProps) => {
  const map = useMapContext();
  const markerRef = useRef<MapLibreMarker | null>(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    const [lng, lat] = position;
    if (
      !Number.isFinite(lng) ||
      !Number.isFinite(lat) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      return;
    }

    const el = document.createElement('div');
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.cursor = draggable ? 'grab' : onClick ? 'pointer' : 'default';
    
    if (iconType === 'courier') {
      el.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="${size}" height="${size}" style="transform: rotate(${bearing !== null ? bearing : 0}deg); transition: transform 0.3s ease;">
          <defs>
            <linearGradient id="courierGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#F97316"/>
              <stop offset="100%" style="stop-color:#EA580C"/>
            </linearGradient>
            <filter id="shadow3" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="32" cy="32" r="30" fill="url(#courierGrad3)" filter="url(#shadow3)"/>
          <circle cx="32" cy="32" r="27" fill="none" stroke="white" stroke-width="2" opacity="0.3"/>
          <circle cx="36" cy="20" r="6" fill="white"/>
          <path d="M30 18 Q36 14 42 18" fill="#EA580C" stroke="none"/>
          <ellipse cx="30" cy="30" rx="8" ry="6" fill="white" transform="rotate(-15 30 30)"/>
          <rect x="18" y="22" width="10" height="10" rx="2" fill="white"/>
          <rect x="20" y="24" width="6" height="2" rx="0.5" fill="#EA580C"/>
          <rect x="20" y="28" width="4" height="1.5" rx="0.5" fill="#EA580C"/>
          <path d="M22 40 L28 36 L44 36 L48 40" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>
          <circle cx="22" cy="44" r="6" fill="none" stroke="white" stroke-width="3"/>
          <circle cx="22" cy="44" r="2" fill="white"/>
          <circle cx="46" cy="44" r="6" fill="none" stroke="white" stroke-width="3"/>
          <circle cx="46" cy="44" r="2" fill="white"/>
          <path d="M38 26 L44 32" stroke="white" stroke-width="3" stroke-linecap="round"/>
        </svg>
      `;
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.transformOrigin = 'center center';
    } else if (iconType === 'restaurant') {
      el.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="${size}" height="${size}">
          <defs>
            <linearGradient id="restaurantGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#22C55E"/>
              <stop offset="100%" style="stop-color:#16A34A"/>
            </linearGradient>
            <filter id="shadowR" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="32" cy="32" r="30" fill="url(#restaurantGrad)" filter="url(#shadowR)"/>
          <ellipse cx="32" cy="34" rx="18" ry="16" fill="white" opacity="0.2"/>
          <ellipse cx="32" cy="34" rx="14" ry="12" fill="none" stroke="white" stroke-width="1.5" opacity="0.4"/>
          <g transform="translate(22, 14)">
            <rect x="0" y="18" width="3" height="20" rx="1.5" fill="white"/>
            <rect x="-2" y="0" width="2" height="12" rx="1" fill="white"/>
            <rect x="1" y="0" width="2" height="14" rx="1" fill="white"/>
            <rect x="4" y="0" width="2" height="12" rx="1" fill="white"/>
            <path d="M-2 12 Q1.5 16 5 12 L5 18 L-1 18 Z" fill="white"/>
          </g>
          <g transform="translate(38, 14)">
            <rect x="0" y="18" width="3" height="20" rx="1.5" fill="white"/>
            <path d="M0 0 L3 0 L5 14 L3 18 L0 18 Z" fill="white"/>
            <line x1="3" y1="2" x2="4" y2="14" stroke="#16A34A" stroke-width="0.5" opacity="0.5"/>
          </g>
        </svg>
      `;
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
    } else if (iconType === 'delivery' || iconType === 'home') {
      el.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="${size}" height="${size}">
          <defs>
            <linearGradient id="deliveryGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#3B82F6"/>
              <stop offset="100%" style="stop-color:#1E40AF"/>
            </linearGradient>
            <filter id="shadowD3" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
            </filter>
          </defs>
          <circle cx="32" cy="32" r="30" fill="url(#deliveryGrad3)" filter="url(#shadowD3)"/>
          <circle cx="32" cy="32" r="27" fill="none" stroke="white" stroke-width="2" opacity="0.3"/>
          <path d="M32 50 C32 50 46 36 46 26 C46 18.268 39.732 12 32 12 C24.268 12 18 18.268 18 26 C18 36 32 50 32 50Z" fill="white"/>
          <circle cx="32" cy="26" r="10" fill="#DBEAFE"/>
          <g transform="translate(32, 26)">
            <path d="M-7 0 L0 -6 L7 0 Z" fill="#1E40AF"/>
            <rect x="-5" y="0" width="10" height="7" fill="#1E40AF"/>
            <rect x="-1.5" y="2" width="3" height="5" fill="#DBEAFE"/>
          </g>
          <circle cx="32" cy="26" r="12" fill="none" stroke="white" stroke-width="1" opacity="0.4"/>
        </svg>
      `;
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
    } else {
      el.style.borderRadius = '50%';
      el.style.backgroundColor = color;
      el.style.border = '3px solid white';
      el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
    }

    const markerOptions: MarkerOptions = {
      element: el,
      anchor: 'center',
      draggable,
    };

    const marker = new MapLibreMarker(markerOptions)
      .setLngLat(position)
      .addTo(map);

    if (popupHtml || popup) {
      const popupElement = new Popup({
        offset: 25,
        maxWidth: '320px',
        className: 'maplibre-popup-custom',
      });
      if (popupHtml) {
        popupElement.setHTML(popupHtml);
      } else {
        popupElement.setText(popup!);
      }
      marker.setPopup(popupElement);
    }

    if (onClick) {
      el.addEventListener('click', onClick);
    }

    if (draggable && onDragEnd) {
      marker.on('dragend', () => {
        const lngLat = marker.getLngLat();
        onDragEnd([lngLat.lng, lngLat.lat]);
      });
    }

    markerRef.current = marker;

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, [map, position, color, size, onClick, popup, popupHtml, draggable, onDragEnd, iconType, bearing]);

  useEffect(() => {
    if (!markerRef.current) {
      return;
    }
    const [lng, lat] = position;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    markerRef.current.setLngLat(position);
  }, [position]);

  return null;
};
